import { PlatformType } from '@src/container/type';
import { FS_ENV_ID } from '../constants';
import { useCallback, useContext, useRef } from 'react';
import { AppContext } from '../provider/AppContext';
import { SEARCH_PARAM_OAUTH_CODE_V1 } from '@src/shared/constants';
import { EventDataFromIframe } from '@src/container/handleIframeMessage/types';
import { sendMessageFromIframe } from '@src/container/handleIframeMessage/handleIframeMessage';

export function useFsEnvIdQueryParam() {
  const params = new URL(document.location.toString()).searchParams;
  return params.get(FS_ENV_ID);
}

export function usePlatformQueryParam(): PlatformType {
  const params = new URL(document.location.toString()).searchParams;
  return (params.get('platformType') || PlatformType.both) as PlatformType;
}
export function useFsUrlQueryParam() {
  const params = new URL(document.location.toString()).searchParams;
  return params.get('fsUrl');
}
export function useTagUrlQueryParam() {
  const params = new URL(document.location.toString()).searchParams;
  return params.get('tagUrl');
}
export function useTagOAuthCodeV1QueryParam() {
  const params = new URL(document.location.toString()).searchParams;
  return params.get(SEARCH_PARAM_OAUTH_CODE_V1);
}

export function useAppContext() {
  return useContext(AppContext);
}

export function usePlatform(): PlatformType {
  const context = useAppContext();
  return context.state.platformType || PlatformType.both;
}

/**
 *  Hook to send message from iframe after a delay
 * @returns
 */
export function useSendMessageFromIframe() {
  const sendImmediately = useCallback((data: EventDataFromIframe) => {
    sendMessageFromIframe(data);
  }, []);
  return { sendImmediately };
}

/**
 * Hook for managing timeouts with automatic cleanup of previous pending timeouts
 *
 * This hook provides a way to set timeouts that automatically cancel any previously
 * pending timeout when a new one is set, creating a debouncing effect. Useful for
 * scenarios like delaying API calls, search queries, or UI updates.
 *
 * **Note:** This hook does NOT automatically cleanup on unmount. Use `useTimeoutWithCleanup`
 * if you need automatic cleanup when the component unmounts.
 *
 *
 * @returns An object with `set` and `clearTimer` methods
 * - `set(callback, delay)` - Sets a timeout, clearing any previous pending timeout
 * - `clearTimer()` - Manually clears the current timeout if one exists
 */
export const useTimeout = () => {
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const set = useCallback(
    (callback: () => void, delay: number) => {
      clearTimer();
      timeoutIdRef.current = setTimeout(callback, delay);
    },
    [clearTimer]
  );

  return { set, clearTimer };
};
